import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Button,
  CircularProgress,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Analytics,
  TrendingUp,
  People,
  Schedule,
  AttachMoney,
  Assignment,
  BarChart,
  PieChart as PieChartIcon,
  CalendarToday,
  AccessTime
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart as RechartsBarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  AreaChart,
  Area
} from 'recharts';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function StudioAnalytics() {
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    endDate: new Date()
  });

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = {
        startDate: dateRange.startDate.toISOString().split('T')[0],
        endDate: dateRange.endDate.toISOString().split('T')[0]
      };
      
      const response = await api.get('/reports/studio-analytics', { params });
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-NG').format(value || 0);
  };

  // Process booking patterns for heatmap
  const processBookingPatterns = (patterns) => {
    const heatmapData = [];
    const hourlyData = {};

    patterns.forEach(pattern => {
      const key = `${pattern.hour}`;
      if (!hourlyData[key]) {
        hourlyData[key] = { hour: parseInt(pattern.hour), count: 0 };
      }
      hourlyData[key].count += pattern.booking_count;
    });

    return Object.values(hourlyData).sort((a, b) => a.hour - b.hour);
  };

  // Process trends for area chart
  const processTrends = (trends) => {
    const trendMap = {};
    
    trends.forEach(trend => {
      if (!trendMap[trend.month]) {
        trendMap[trend.month] = {
          month: trend.month,
          'Recording Studio': 0,
          'Photo Studio': 0,
          'Outside Recording': 0
        };
      }
      trendMap[trend.month][trend.department] = parseFloat(trend.revenue);
    });

    return Object.values(trendMap).sort((a, b) => a.month.localeCompare(b.month));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!analyticsData) return null;

  const { utilization, bookingPatterns, trends, topClients } = analyticsData;
  const hourlyBookings = bookingPatterns ? processBookingPatterns(bookingPatterns) : [];
  const trendData = trends ? processTrends(trends) : [];

  return (
    <Box>
      {/* Date Range Selector */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={dateRange.startDate}
                onChange={(value) => setDateRange({ ...dateRange, startDate: value })}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={dateRange.endDate}
                onChange={(value) => setDateRange({ ...dateRange, endDate: value })}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              fullWidth
              onClick={fetchAnalytics}
              startIcon={<Analytics />}
            >
              Update Analytics
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Department Utilization Cards */}
      <Typography variant="h6" gutterBottom>
        Department Performance Overview
      </Typography>
      <Grid container spacing={3} mb={3}>
        {utilization.map((dept, index) => (
          <Grid item xs={12} md={4} key={dept.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="h6" color="primary">
                      {dept.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Department Analytics
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: COLORS[index % COLORS.length] }}>
                    <BarChart />
                  </Avatar>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Total Projects
                    </Typography>
                    <Typography variant="h5">
                      {dept.total_projects}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Unique Clients
                    </Typography>
                    <Typography variant="h5">
                      {dept.unique_clients}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Active Days
                    </Typography>
                    <Typography variant="h5">
                      {dept.active_days}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Avg Duration
                    </Typography>
                    <Typography variant="h5">
                      {Math.round(dept.avg_project_duration)} days
                    </Typography>
                  </Grid>
                </Grid>

                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Utilization Rate
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(dept.active_days / 30) * 100} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    {Math.round((dept.active_days / 30) * 100)}% of period
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Revenue Trends */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Revenue Trends by Department
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `₦${(value / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Area type="monotone" dataKey="Recording Studio" stackId="1" stroke="#8884d8" fill="#8884d8" />
                <Area type="monotone" dataKey="Photo Studio" stackId="1" stroke="#82ca9d" fill="#82ca9d" />
                <Area type="monotone" dataKey="Outside Recording" stackId="1" stroke="#ffc658" fill="#ffc658" />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Peak Hours */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Peak Booking Hours
            </Typography>
            {hourlyBookings.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RechartsBarChart data={hourlyBookings}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="hour" 
                    tickFormatter={(hour) => `${hour}:00`}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(hour) => `${hour}:00`}
                    formatter={(value) => [`${value} bookings`, 'Count']}
                  />
                  <Bar dataKey="count" fill="#8884d8" />
                </RechartsBarChart>
              </ResponsiveContainer>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <Typography color="textSecondary">No booking data available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Top Clients by Department */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Clients by Department
            </Typography>
            <Grid container spacing={3}>
              {['Recording Studio', 'Photo Studio', 'Outside Recording'].map((deptName, index) => {
                const deptClients = topClients
                  .filter(client => client.department === deptName)
                  .slice(0, 5);

                return (
                  <Grid item xs={12} md={4} key={deptName}>
                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                      {deptName}
                    </Typography>
                    {deptClients.length > 0 ? (
                      <List dense>
                        {deptClients.map((client, idx) => (
                          <React.Fragment key={idx}>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: COLORS[index], width: 32, height: 32 }}>
                                  {idx + 1}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={client.client}
                                secondary={`${client.project_count} projects`}
                              />
                              <Typography variant="body2" fontWeight="medium">
                                {formatCurrency(client.total_revenue)}
                              </Typography>
                            </ListItem>
                            {idx < deptClients.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                        No client data available
                      </Typography>
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>

        {/* Department Comparison Radar Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Department Performance Comparison
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={utilization.map(dept => ({
                department: dept.name,
                projects: (dept.total_projects / Math.max(...utilization.map(d => d.total_projects))) * 100,
                clients: (dept.unique_clients / Math.max(...utilization.map(d => d.unique_clients))) * 100,
                utilization: (dept.active_days / 30) * 100,
                efficiency: Math.min(100, (dept.avg_project_duration ? 30 / dept.avg_project_duration : 0) * 100)
              }))}>
                <PolarGrid />
                <PolarAngleAxis dataKey="department" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} />
                <Radar name="Projects" dataKey="projects" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                <Radar name="Clients" dataKey="clients" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                <Radar name="Utilization" dataKey="utilization" stroke="#ffc658" fill="#ffc658" fillOpacity={0.6} />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Key Metrics Summary */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Key Performance Indicators
            </Typography>
            <List>
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <Assignment />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Total Projects"
                  secondary={`Across all departments`}
                />
                <Typography variant="h6">
                  {utilization.reduce((sum, dept) => sum + dept.total_projects, 0)}
                </Typography>
              </ListItem>
              <Divider />
              
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <People />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Total Unique Clients"
                  secondary={`Served during period`}
                />
                <Typography variant="h6">
                  {utilization.reduce((sum, dept) => sum + dept.unique_clients, 0)}
                </Typography>
              </ListItem>
              <Divider />
              
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'warning.main' }}>
                    <Schedule />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Average Project Duration"
                  secondary={`Days per project`}
                />
                <Typography variant="h6">
                  {Math.round(
                    utilization.reduce((sum, dept) => sum + dept.avg_project_duration, 0) / 
                    utilization.length
                  )} days
                </Typography>
              </ListItem>
              <Divider />
              
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <CalendarToday />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Total Active Days"
                  secondary={`Combined operational days`}
                />
                <Typography variant="h6">
                  {utilization.reduce((sum, dept) => sum + dept.active_days, 0)}
                </Typography>
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default StudioAnalytics;
